/**
 * Content Rules Service
 * API service for Content Rule Center feature
 */
import {
  CONTENT_RULES_CATEGORIES,
  CONTENT_RULES_SEVERITY_SUMMARY,
  CONTENT_RULES_CATEGORY_SUMMARY,
  GET_CONTENT_RULES,
} from '../constants/apiRoutes';
import { ApiError } from '../types/Content';
import {
  CategoriesResponse,
  SeveritySummaryResponse,
  CategorySummaryResponse,
  ContentRulesResponse,
  GetContentRulesParams,
} from '../types/ContentRules';
import { httpClient } from '../utils/httpClient';
import Logger from '../utils/logger';

/**
 * Generic error handler for Content Rules API errors
 */
const handleError = <T>(defaultData: T): { success: false; data: T } => {
  return {
    success: false,
    data: defaultData,
  };
};

/**
 * Fetches available content rule categories
 * GET /api/marketing/content/v1/content-rules/metadata/categories
 */
export const fetchContentRulesCategories =
  async (): Promise<CategoriesResponse> => {
    try {
      const response = await httpClient.get<CategoriesResponse>(
        CONTENT_RULES_CATEGORIES
      );

      const { data, success } = response as CategoriesResponse;
      if (success && data) {
        return {
          success: true,
          data,
        };
      }
      return handleError({ items: [] });
    } catch (error) {
      const fetchError = error as ApiError;
      Logger.error(new Error(fetchError.message));
      return handleError({ items: [] });
    }
  };

/**
 * Fetches severity-wise summary of content rules
 * GET /api/marketing/content/v1/content-rules/summary/severity-wise
 */
export const fetchSeverityWiseSummary =
  async (): Promise<SeveritySummaryResponse> => {
    try {
      const response = await httpClient.get<SeveritySummaryResponse>(
        CONTENT_RULES_SEVERITY_SUMMARY
      );

      const { data, success, message, errors } =
        response as SeveritySummaryResponse;
      if (success && data) {
        return {
          success: true,
          message: message || '',
          errors: errors || [],
          data,
        };
      }
      return {
        success: false,
        message: message || 'Failed to fetch severity summary',
        errors: errors || [],
        data: { totalRuleCount: '0', severityRuleCounts: [] },
      };
    } catch (error) {
      const fetchError = error as ApiError;
      Logger.error(new Error(fetchError.message));
      return {
        success: false,
        message: 'An error occurred while fetching severity summary',
        errors: [fetchError.message],
        data: { totalRuleCount: '0', severityRuleCounts: [] },
      };
    }
  };

/**
 * Fetches category-wise summary of content rules
 * GET /api/marketing/content/v1/content-rules/summary/category-wise
 */
export const fetchCategoryWiseSummary = async (params?: {
  countries?: string;
  brands?: string;
  audience?: string;
  channels?: string;
  categories?: string;
}): Promise<CategorySummaryResponse> => {
  try {
    let url = CONTENT_RULES_CATEGORY_SUMMARY;

    if (params) {
      const queryParams: string[] = [];
      if (params.countries)
        queryParams.push(`countries=${encodeURIComponent(params.countries)}`);
      if (params.brands)
        queryParams.push(`brands=${encodeURIComponent(params.brands)}`);
      if (params.audience)
        queryParams.push(`audience=${encodeURIComponent(params.audience)}`);
      if (params.channels)
        queryParams.push(`channels=${encodeURIComponent(params.channels)}`);
      if (params.categories)
        queryParams.push(`categories=${encodeURIComponent(params.categories)}`);

      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
    }

    const response = await httpClient.get<CategorySummaryResponse>(url);

    const { data, success, message, errors } =
      response as CategorySummaryResponse;
    if (success && data) {
      return {
        success: true,
        message: message || '',
        errors: errors || [],
        data,
      };
    }
    return {
      success: false,
      message: message || 'Failed to fetch category summary',
      errors: errors || [],
      data: { categoryRuleCounts: [] },
    };
  } catch (error) {
    const fetchError = error as ApiError;
    Logger.error(new Error(fetchError.message));
    return {
      success: false,
      message: 'An error occurred while fetching category summary',
      errors: [fetchError.message],
      data: { categoryRuleCounts: [] },
    };
  }
};

/**
 * Fetches content rules with optional filters and pagination
 * GET /api/marketing/content/v1/content-rules/rules
 */
export const fetchContentRules = async (
  params: GetContentRulesParams = {}
): Promise<ContentRulesResponse> => {
  try {
    const url = GET_CONTENT_RULES(params);
    const response = await httpClient.get<ContentRulesResponse>(url);

    const { data, success, message, errors } = response as ContentRulesResponse;
    if (success && data) {
      return {
        success: true,
        message: message || 'Content rules fetched successfully',
        errors: errors || [],
        data,
      };
    }
    return {
      success: false,
      message: message || 'Failed to fetch content rules',
      errors: errors || [],
      data: {
        rules: [],
        pagination: {
          page: 1,
          pageSize: 20,
          totalPages: 0,
          total: 0,
          nextPage: null,
          previousPage: null,
        },
      },
    };
  } catch (error) {
    const fetchError = error as ApiError;
    Logger.error(new Error(fetchError.message));
    return {
      success: false,
      message: 'An error occurred while fetching content rules',
      errors: [fetchError.message],
      data: {
        rules: [],
        pagination: {
          page: 1,
          pageSize: 20,
          totalPages: 0,
          total: 0,
          nextPage: null,
          previousPage: null,
        },
      },
    };
  }
};
